import { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import AppContext from "./contexts/AppContext";
import PopupModal from "./components/PopupModal";
import SearchIndex from "./components/sodoSearch/search-index";
import { PublicationsProvider } from "./contexts/WithPublications/PublicationsProvider";

interface AppProps {
  adminUrl: string;
  apiKey?: string;
  stylesUrl?: string;
}

declare global {
  interface Window {
    frjToken: string;
    ghostApiKey?: string;
    algoliaApiKey: string;
    algoliaAppIdKey: string;
    algoliaIndexName: string;
    compassUrl: string;
  }
}

export default function App({ adminUrl, apiKey, stylesUrl }: AppProps) {
  const [searchIndex] = useState(
    () => new SearchIndex({ adminUrl, apiKey: apiKey ?? "" }),
  );
  const [showPopup, setShowPopup] = useState(false);
  const [indexStarted, setIndexStarted] = useState(false);
  const [indexComplete, setIndexComplete] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const bodyScrollRef = useRef("");

  // Lock body scroll when popup is open
  useEffect(() => {
    if (showPopup) {
      bodyScrollRef.current = window.document?.body?.style?.overflow ?? "";
      window.document.body.style.overflow = "hidden";
    } else {
      window.document.body.style.overflow = bodyScrollRef.current;
      setSearchValue("");
    }
  }, [showPopup]);

  // Initialise search index on first open
  useEffect(() => {
    if (showPopup && !indexStarted) {
      setIndexStarted(true);
      searchIndex.init().then(() => setIndexComplete(true));
    }
  }, [showPopup, indexStarted, searchIndex]);

  const handleSearchUrl = useCallback(() => {
    const [path] = window.location.hash.substring(1).split("?");
    if (path === "/search" || path === "/search/") {
      setShowPopup(true);
      window.history.replaceState("", document.title, window.location.pathname);
    }
  }, []);

  // Handle #/search URL and hash changes
  useEffect(() => {
    handleSearchUrl();
    window.addEventListener("hashchange", handleSearchUrl, false);
    return () =>
      window.removeEventListener("hashchange", handleSearchUrl, false);
  }, [handleSearchUrl]);

  // Wire up custom [data-ghost-search] trigger buttons
  useEffect(() => {
    const buttons = Array.from(
      document.querySelectorAll<HTMLElement>("[data-ghost-search]"),
    );
    const handleClick = (e: Event) => {
      e.preventDefault();
      setShowPopup(true);
    };
    buttons.forEach((btn) => btn.addEventListener("click", handleClick));
    return () =>
      buttons.forEach((btn) => btn.removeEventListener("click", handleClick));
  }, []);

  // Cmd+K keyboard shortcut (only when custom trigger buttons exist)
  useEffect(() => {
    const buttons = document.querySelectorAll("[data-ghost-search]");
    if (!buttons.length) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && e.metaKey) {
        setShowPopup(true);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const dispatch = useCallback(
    (
      action: "update",
      data: {
        showPopup?: boolean;
        searchValue?: string;
        indexComplete?: boolean;
      },
    ) => {
      if (action === "update") {
        if (data.showPopup !== undefined) {
          setShowPopup(data.showPopup);
        }
        if (data.searchValue !== undefined) {
          setSearchValue(data.searchValue);
        }
        if (data.indexComplete !== undefined) {
          setIndexComplete(data.indexComplete);
        }
      }
    },
    [],
  );

  return (
    <AppContext.Provider
      value={{
        page: "search",
        posts: [],
        authors: [],
        tags: [],
        action: "",
        lastPage: "",
        pageData: {},
        showPopup,
        adminUrl,
        stylesUrl,
        searchIndex,
        indexComplete,
        searchValue,
        onAction: () => {},
        dispatch,
      }}
    >
      <PublicationsProvider>
        <PopupModal />
      </PublicationsProvider>
    </AppContext.Provider>
  );
}
